/**
 * AdminTable — shared raw-`<table>` shell for the admin management surfaces
 * (STORY-008-007). Generalises the shipped `QueueListPage` table markup so the
 * sellers / products / users lists share one responsive, accessible scaffold:
 *
 *   - Desktop (`hidden sm:block`): a bordered, horizontally-scrollable `<table>`
 *     with a tokenised header; numeric columns right-align + `tabular-nums`.
 *   - Mobile (`sm:hidden`): a card stack — each page supplies `renderMobileCard`.
 *   - Rows are `role="button" tabIndex={0}` with Enter/Space activation (the real
 *     drill-in is a drawer the page owns via `onRowActivate`).
 *   - Loading → `Skeleton` rows; error → tokenised line; empty → a `data-testid`
 *     `${testid}-empty` line (the four mandatory states from M1-design.md).
 *   - Optional `selection` adds a leading checkbox column (header = select-all on
 *     the loaded page) for the products bulk toolbar. Checkbox clicks never
 *     bubble into the row's drill-in.
 *
 * `--g-*` tokens only; ≥44px targets; design per
 * `docs/superpowers/plans/grounding/M1-design.md`.
 */
import { type KeyboardEvent, type ReactElement, type ReactNode } from "react";

import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from "lucide-react";

import { Skeleton } from "@garden/ui";

export interface AdminTableColumn {
  /** Stable column key passed back to `renderCell`. */
  key: string;
  /** i18n key for the `<th>` label (already namespaced by the caller). */
  label: string;
  /** Right-align + tabular figures for numeric columns. */
  align?: "left" | "right";
  /** Extra classes for the `<td>` (e.g. width hints). */
  cellClassName?: string;
  /** Opt this column into the header sort control (SCRUM-154/161). Ignored
   * unless the caller also passes `AdminTableProps.sort`. */
  sortable?: boolean;
}

/** Header click-to-sort state (SCRUM-154/161) — opt-in, backward compatible.
 * Callers that omit `sort` (every pre-existing consumer) render the exact
 * same plain `<th>{label}</th>` as before. */
export interface AdminTableSort {
  /** Currently sorted column key, or `null` for the loaded (unsorted) order. */
  key: string | null;
  direction: "asc" | "desc";
  /** Fired on header click/Enter/Space for a `sortable` column. The page
   * owns the toggle logic (same key → flip direction; new key → default). */
  onSort: (key: string) => void;
  /** `aria-label` builder for the sort button, e.g. "Name, sorted ascending". */
  ariaLabel: (column: AdminTableColumn, direction: "asc" | "desc" | null) => string;
}

export interface AdminTableSelection<T> {
  /** Currently-selected row ids. */
  selectedIds: ReadonlySet<string>;
  /** Toggle one row id. */
  onToggle: (id: string) => void;
  /** Toggle every loaded row id (select-all / clear). */
  onToggleAll: () => void;
  /** `aria-label` for the header select-all checkbox. */
  selectAllLabel: string;
  /** Per-row checkbox `aria-label`. */
  rowLabel: (row: T) => string;
}

export interface AdminTableProps<T> {
  columns: readonly AdminTableColumn[];
  rows: readonly T[];
  /** Stable id for React keys AND selection. */
  rowKey: (row: T) => string;
  /** Desktop `<td>` content for `(row, column.key)`. */
  renderCell: (row: T, columnKey: string) => ReactNode;
  /** Mobile card body for one row. */
  renderMobileCard: (row: T) => ReactNode;
  /** Drill-in (open the detail drawer). Omit for a read-only table. */
  onRowActivate?: (row: T) => void;
  /**
   * How a row exposes its drill-in affordance.
   *  - `"row"` (default): the whole `<tr>` / card is `role="button" tabIndex={0}`.
   *    Use only when NO cell renders its own focusable control.
   *  - `"cell"`: the row is NOT `role="button"`; instead the first cell hosts an
   *    explicit open-`<button>` (desktop) / a trailing chevron open-`<button>`
   *    (mobile). Use when a cell hosts an interactive control (e.g. an inline
   *    toggle) so the activatable surface does not nest another focusable
   *    control (axe `nested-interactive`). This mirrors the `selection` layout.
   */
  rowActivation?: "row" | "cell";
  /** Accessible row label (`aria-label` on the activatable row/card). */
  rowAriaLabel: (row: T) => string;
  testid: string;
  isLoading: boolean;
  isError: boolean;
  emptyLabel: string;
  errorLabel: string;
  selection?: AdminTableSelection<T>;
  /** Click-to-sort header state (SCRUM-154/161). Omit for a static
   * (unsortable) table — every existing consumer keeps its current
   * plain-text headers. */
  sort?: AdminTableSort;
}

function onActivateKey<T>(
  row: T,
  onRowActivate: (row: T) => void,
): (e: KeyboardEvent) => void {
  return (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowActivate(row);
    }
  };
}

export function AdminTable<T>({
  columns,
  rows,
  rowKey,
  renderCell,
  renderMobileCard,
  onRowActivate,
  rowActivation = "row",
  rowAriaLabel,
  testid,
  isLoading,
  isError,
  emptyLabel,
  errorLabel,
  selection,
  sort,
}: AdminTableProps<T>): ReactElement {
  // A cell hosting its own focusable control (e.g. an inline toggle) means the
  // row cannot be `role="button"` — so route activation through an explicit
  // open-button instead. `selection` already does this; `cellActivation` is the
  // same layout without the leading checkbox column.
  const cellActivation = rowActivation === "cell";
  const useOpenButton = selection !== undefined || cellActivation;
  if (isLoading) {
    return (
      <div data-testid={`${testid}-loading`} className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-[var(--g-radius-md)]" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p
        data-testid={`${testid}-error`}
        role="alert"
        className="py-6 text-sm text-[var(--g-error)]"
      >
        {errorLabel}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p
        data-testid={`${testid}-empty`}
        className="py-8 text-center text-sm text-[var(--g-secondary)]"
      >
        {emptyLabel}
      </p>
    );
  }

  const allSelected =
    selection !== undefined &&
    rows.length > 0 &&
    rows.every((r) => selection.selectedIds.has(rowKey(r)));

  return (
    <>
      {/* Desktop table (≥640px) */}
      <div className="hidden overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)] sm:block">
        <table
          data-testid={`${testid}-table`}
          className="w-full min-w-[640px] border-collapse bg-[var(--g-surface)] text-sm"
        >
          <thead className="bg-[var(--g-elevated)]">
            <tr>
              {selection ? (
                <th scope="col" className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    data-testid={`${testid}-select-all`}
                    aria-label={selection.selectAllLabel}
                    checked={allSelected}
                    onChange={selection.onToggleAll}
                    className="size-5 accent-[var(--g-primary)]"
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const isSortable = sort !== undefined && col.sortable === true;
                const isActive = isSortable && sort!.key === col.key;
                const direction = isActive ? sort!.direction : null;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      isSortable
                        ? direction === "asc"
                          ? "ascending"
                          : direction === "desc"
                            ? "descending"
                            : "none"
                        : undefined
                    }
                    className={[
                      "px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]",
                      col.align === "right" ? "text-right" : "text-left",
                    ].join(" ")}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        data-testid={`${testid}-sort-${col.key}`}
                        aria-label={sort!.ariaLabel(col, direction)}
                        onClick={() => sort!.onSort(col.key)}
                        className={[
                          "inline-flex items-center gap-1 rounded-[var(--g-radius-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                          col.align === "right" ? "flex-row-reverse" : "",
                        ].join(" ")}
                      >
                        <span>{col.label}</span>
                        {direction === "asc" ? (
                          <ChevronUp className="size-3.5" aria-hidden="true" />
                        ) : direction === "desc" ? (
                          <ChevronDown className="size-3.5" aria-hidden="true" />
                        ) : (
                          <ChevronsUpDown className="size-3.5 opacity-40" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = rowKey(row);
              const activate = onRowActivate;
              // A `role="button"` row may NOT contain another focusable control
              // (axe `nested-interactive`). So when the table is selectable OR a
              // cell hosts an interactive control (`rowActivation="cell"`), the
              // row is a plain (mouse) onClick and the keyboard drill-in lives in
              // the first cell as a sibling <button>. Otherwise we keep the
              // shipped whole-row button.
              return (
                <tr
                  key={id}
                  data-testid={`${testid}-row-${id}`}
                  {...(activate && !useOpenButton
                    ? {
                        onClick: () => activate(row),
                        onKeyDown: onActivateKey(row, activate),
                        tabIndex: 0,
                        role: "button",
                        "aria-label": rowAriaLabel(row),
                      }
                    : activate
                      ? { onClick: () => activate(row) }
                      : {})}
                  className={[
                    "border-b border-[var(--g-hairline)]",
                    activate
                      ? "cursor-pointer hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
                      : "",
                  ].join(" ")}
                >
                  {selection ? (
                    <td
                      className="px-3 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        data-testid={`${testid}-select-${id}`}
                        aria-label={selection.rowLabel(row)}
                        checked={selection.selectedIds.has(id)}
                        onChange={() => selection.onToggle(id)}
                        className="size-5 accent-[var(--g-primary)]"
                      />
                    </td>
                  ) : null}
                  {columns.map((col, idx) => (
                    <td
                      key={col.key}
                      className={[
                        "px-3 py-3",
                        col.align === "right"
                          ? "text-right tabular-nums"
                          : "text-left",
                        col.cellClassName ?? "",
                      ].join(" ")}
                    >
                      {activate && useOpenButton && idx === 0 ? (
                        <button
                          type="button"
                          data-testid={`${testid}-open-${id}`}
                          aria-label={rowAriaLabel(row)}
                          onClick={(e) => {
                            e.stopPropagation();
                            activate(row);
                          }}
                          className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                        >
                          {renderCell(row, col.key)}
                        </button>
                      ) : (
                        renderCell(row, col.key)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card stack (<640px) */}
      <div
        data-testid={`${testid}-cards`}
        className="flex flex-col gap-2 sm:hidden"
      >
        {rows.map((row) => {
          const id = rowKey(row);
          return (
            <div
              key={id}
              data-testid={`${testid}-card-${id}`}
              className="flex items-start gap-3 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 shadow-[var(--g-shadow-sm)]"
            >
              {selection ? (
                <input
                  type="checkbox"
                  data-testid={`${testid}-card-select-${id}`}
                  aria-label={selection.rowLabel(row)}
                  checked={selection.selectedIds.has(id)}
                  onChange={() => selection.onToggle(id)}
                  className="mt-1 size-5 shrink-0 accent-[var(--g-primary)]"
                />
              ) : null}
              {/* In `cellActivation` mode the card body may host its own focusable
                  control, so it must NOT be `role="button"` (axe
                  `nested-interactive`). The drill-in lives in a trailing
                  open-`<button>` sibling instead. */}
              <div
                {...(onRowActivate && !cellActivation
                  ? {
                      role: "button",
                      tabIndex: 0,
                      "aria-label": rowAriaLabel(row),
                      onClick: () => onRowActivate(row),
                      onKeyDown: onActivateKey(row, onRowActivate),
                    }
                  : {})}
                className={[
                  "flex-1 rounded-[var(--g-radius-sm)]",
                  onRowActivate !== undefined && !cellActivation
                    ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                    : "",
                ].join(" ")}
              >
                {renderMobileCard(row)}
              </div>
              {onRowActivate && cellActivation ? (
                <button
                  type="button"
                  data-testid={`${testid}-card-open-${id}`}
                  aria-label={rowAriaLabel(row)}
                  onClick={() => onRowActivate(row)}
                  className="-mr-1 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--g-radius-sm)] text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                >
                  <ChevronRight className="size-5" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default AdminTable;
